import React, { useState } from 'react';
import { api } from '../api.js';
import { Card, ErrorBanner, Loading, ProofReference, StateChip, useAsync, useSession, useToast } from '../ui.jsx';
import { auditLabel, dateLabel, money, monthLabel, nameOf, timestampLabel, unitName } from '../lib/format.js';
import LedgerFilters, { defaultFilters } from './ledger-filters.jsx';
import { buildLedgerPdf, buildLedgerWorkbook, ledgerFileName } from '../lib/exports.js';
import { downloadBlob } from '../lib/xlsx.js';
import { Link } from '../main.jsx';

/**
 * The complete 帳簿 history: 付款項目, 付款提交, 調整應付款, 結餘, 按金權益, 歷史補錄
 * and audit events, plus the Excel and PDF exports generated in the browser from
 * this already-authorized data.
 */
export default function ConsoleHistory() {
  const session = useSession();
  const notify = useToast();
  const [filters, setFilters] = useState(defaultFilters);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState('');
  const ledger = useAsync(() => api.ledger(filters), [JSON.stringify(filters)]);
  const exports = useAsync(() => api.exports(), []);

  const runExport = async (format) => {
    setBusy(format);
    setError(null);
    try {
      const data = ledger.data;
      const fileName = ledgerFileName(data, format === 'excel' ? 'xlsx' : 'pdf');
      const bytes = format === 'excel'
        ? buildLedgerWorkbook(data, session.member.display_name)
        : await buildLedgerPdf(data, session.member.display_name);
      await api.recordExport({
        format,
        filters: data.filters,
        row_count: data.overview.rows.length,
        file_name: fileName,
      });
      downloadBlob(
        bytes,
        fileName,
        format === 'excel'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/pdf',
      );
      notify(`${format === 'excel' ? 'Excel 帳簿' : 'PDF 統計表'}已產生並記錄匯出`);
      exports.reload();
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <div className="eyebrow">帳簿控制台</div>
          <h1>帳簿歷史與匯出</h1>
          <p className="small muted">支出依帳務月份，付款依實際確認日期。報表只帶證明參考連結，不嵌入圖片、不列原始檔名。</p>
        </div>
        <div className="actions">
          <button type="button" disabled={!ledger.data || busy !== ''} onClick={() => runExport('excel')} data-testid="export-excel">
            {busy === 'excel' ? '產生中…' : '下載 Excel 帳簿'}
          </button>
          <button type="button" disabled={!ledger.data || busy !== ''} onClick={() => runExport('pdf')} data-testid="export-pdf">
            {busy === 'pdf' ? '產生中…' : '下載 PDF 統計表'}
          </button>
        </div>
      </div>

      <Card title="篩選"><LedgerFilters filters={filters} onChange={setFilters} /></Card>

      <ErrorBanner error={error} />
      {ledger.loading ? <Loading /> : null}
      {ledger.error ? <ErrorBanner error={ledger.error} /> : null}

      {ledger.data ? (
        <>
          <Card title="付款項目" subtitle={`${ledger.data.items.length} 個項目`} testId="history-items">
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>付款項目</th><th>單位</th><th>分類</th><th>帳務月份</th>
                    <th className="num">總額</th><th className="num">已確認</th><th className="num">仍待付款</th>
                    <th>收款人</th><th>確認人</th><th>狀態</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.data.items.map((item) => (
                    <tr key={item.id} data-testid={`history-item-${item.id}`}>
                      <td>
                        <Link to={`/items/${item.id}`}>{item.title}</Link>
                        <div className="small muted">
                          {item.origin === 'recurrence' ? '循環設定產生' : item.origin === 'historical' ? '歷史補錄' : '手動發出'} ·
                          建立者 {nameOf(ledger.data.members, item.creator_member_id)}
                        </div>
                      </td>
                      <td>{unitName(ledger.data.units, item.unit_id)}</td>
                      <td>{item.category}</td>
                      <td>{monthLabel(item.accounting_month)}</td>
                      <td className="num">{money(item.total_cents)}</td>
                      <td className="num">{money(item.confirmed_cents)}</td>
                      <td className="num">{money(item.remaining_cents)}</td>
                      <td>{item.recipient_kind === 'member' ? nameOf(ledger.data.members, item.recipient_member_id) : item.recipient_name}</td>
                      <td>{item.historical ? '—' : nameOf(ledger.data.members, item.confirmer_member_id)}</td>
                      <td>
                        {item.status === 'cancelled'
                          ? <span className="chip cancelled">已取消</span>
                          : item.historical
                            ? <span className="chip settled">{item.historical_outcome === 'settled' ? '歷史 · 已結清' : '歷史 · 已取消'}</span>
                            : <span className="chip">生效中</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <Card title="付款紀錄" subtitle="付款提交按確認日期、歷史付款按實際付款日期" testId="history-payments">
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>證明參考</th><th>付款項目</th><th>成員</th><th className="num">金額</th><th>狀態</th><th>日期</th><th>種類</th></tr>
                </thead>
                <tbody>
                  {ledger.data.payments.length === 0 ? (
                    <tr><td colSpan={7} className="muted small">此期間沒有付款紀錄。</td></tr>
                  ) : null}
                  {ledger.data.payments.map((payment) => (
                    <tr key={payment.reference} data-testid={`history-payment-${payment.reference}`}>
                      <td>{payment.kind === 'submission' ? <ProofReference reference={payment.reference} /> : payment.reference}</td>
                      <td><Link to={`/items/${payment.item_id}`}>{payment.item_title}</Link></td>
                      <td>{nameOf(ledger.data.members, payment.member_id)}</td>
                      <td className="num">{money(payment.amount_cents)}</td>
                      <td><StateChip state={payment.state} label={payment.state_label} /></td>
                      <td>{dateLabel(payment.date)}</td>
                      <td className="small">{payment.kind === 'submission' ? '付款提交' : '歷史付款'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <Card title="調整應付款" testId="history-adjustments">
            {ledger.data.adjustments.length === 0 ? <p className="small muted">此期間沒有調整紀錄。</p> : (
              <div className="table-wrap">
                <table>
                  <thead><tr><th>付款項目</th><th>成員</th><th className="num">舊金額</th><th className="num">新金額</th><th>原因</th><th>操作者</th><th>時間</th></tr></thead>
                  <tbody>
                    {ledger.data.adjustments.map((adjustment) => (
                      <tr key={adjustment.id} data-testid={`history-adjustment-${adjustment.id}`}>
                        <td>{adjustment.item_title}</td>
                        <td>{nameOf(ledger.data.members, adjustment.member_id)}</td>
                        <td className="num">{money(adjustment.old_amount_cents)}</td>
                        <td className="num">{money(adjustment.new_amount_cents)}</td>
                        <td className="small">{adjustment.reason}</td>
                        <td>{nameOf(ledger.data.members, adjustment.actor_member_id)}</td>
                        <td className="small">{timestampLabel(adjustment.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card title="結餘" testId="history-balances">
            {ledger.data.balances.length === 0 ? <p className="small muted">此期間沒有結餘。</p> : (
              <div className="table-wrap">
                <table>
                  <thead><tr><th>付款項目</th><th>成員</th><th className="num">結餘</th><th className="num">待處理</th><th>來源</th><th>處理</th></tr></thead>
                  <tbody>
                    {ledger.data.balances.map((balance) => (
                      <tr key={balance.id} data-testid={`history-balance-${balance.id}`}>
                        <td>{balance.item_title}</td>
                        <td>{nameOf(ledger.data.members, balance.member_id)}</td>
                        <td className="num">{money(balance.amount_cents)}</td>
                        <td className="num">{money(balance.pending_cents)}</td>
                        <td className="small">{balance.origin}</td>
                        <td className="small">
                          {balance.dispositions.length === 0 ? '待處理' : balance.dispositions.map((disposition) => (
                            <div key={disposition.id}>
                              {disposition.kind === 'returned' ? '已退回' : '已轉用'} {money(disposition.amount_cents)} · {dateLabel(disposition.handled_on)}
                            </div>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card title="按金權益" subtitle="日後應獲退回的份額，不視為一般消費" testId="history-deposit">
            <div className="table-wrap">
              <table>
                <thead><tr><th>成員</th><th className="num">按金權益</th></tr></thead>
                <tbody>
                  {ledger.data.overview.member_totals.map((total) => (
                    <tr key={total.member_id}>
                      <td>{nameOf(ledger.data.members, total.member_id)}</td>
                      <td className="num" data-testid={`deposit-${total.member_id}`}>{money(total.deposit_equity_cents)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr><td>合計</td><td className="num">{money(ledger.data.overview.deposit_equity_cents)}</td></tr></tfoot>
              </table>
            </div>
          </Card>

          <Card title="留痕紀錄" subtitle="操作者、UTC 時間、原因及前後內容" testId="history-audit">
            <ul className="audit-list">
              {ledger.data.audit_events.map((event) => (
                <li key={event.id} data-testid={`history-audit-${event.action}`}>
                  <strong>{auditLabel(event.action)}</strong> · {nameOf(ledger.data.members, event.actor_member_id)} · {timestampLabel(event.created_at)}
                  {event.reason ? <div>原因：{event.reason}</div> : null}
                </li>
              ))}
            </ul>
          </Card>

          <Card title="匯出紀錄" subtitle="每次匯出保留操作者、UTC 時間、格式及篩選條件" testId="export-log">
            {exports.data && exports.data.exports.length ? (
              <ul className="audit-list">
                {exports.data.exports.map((event) => (
                  <li key={event.id} data-testid="export-record">
                    {timestampLabel(event.created_at)} · {nameOf(session.members, event.actor_member_id)} ·
                    {(() => { const after = JSON.parse(event.after_json || '{}'); return ` ${after.format === 'excel' ? 'Excel' : 'PDF'} · ${after.file_name || ''}`; })()}
                  </li>
                ))}
              </ul>
            ) : <p className="small muted">尚未有匯出紀錄。</p>}
          </Card>
        </>
      ) : null}
    </div>
  );
}
